import React, { useState, useEffect } from 'react';
import { Dialog } from './shared/Dialog';
import { useInvoiceStore } from '../stores/invoiceStore';
import { invoiceApi } from '../lib/api';
import { motion } from 'framer-motion';
import { useTranslation } from '../hooks/useTranslation';
import {
  Clock,
  AlertCircle,
  RefreshCw,
  FileEdit,
  PlusCircle,
  Save,
  Send,
  Calendar,
  CheckCircle,
  Bell
} from 'lucide-react';
import { formatRelativeTime } from '@dutchinvoice/shared-utils';
import { Timeline, TimelineItem } from './shared/Timeline';

interface HistoryDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

type EmailStatus = 'planned' | 'sent' | 'failed';

interface EmailHistoryItem {
  id: string;
  invoice_id: string;
  user_id: string;
  recipient_email: string;
  status: EmailStatus;
  scheduled_at: string;
  sent_at?: string | null;
  attempts: number;
  email_locale?: string;
  custom_message?: string | null;
  error?: string | null;
  is_reminder?: number;
}

interface InvoiceHistory {
  created: {
    timestamp: string;
    formatted: string;
  };
  updated?: {
    timestamp: string;
    formatted: string;
  };
  saved?: {
    timestamp: string;
    formatted: string;
  };
}

export const HistoryDialog: React.FC<HistoryDialogProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const currentInvoice = useInvoiceStore((state) => state.currentInvoice);
  const settings = useInvoiceStore(state => state.settings);
  const language = settings.language;
  const [emails, setEmails] = useState<EmailHistoryItem[]>([]);
  const [invoiceHistory, setInvoiceHistory] = useState<InvoiceHistory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !currentInvoice.id) return;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Debug: Check if currentInvoice has paidAt
        console.log('[HistoryDialog] Current invoice payment info:', {
          id: currentInvoice.id,
          invoiceNumber: currentInvoice.invoiceNumber,
          paymentStatus: currentInvoice.paymentStatus,
          paidAt: currentInvoice.paidAt
        });
        
        // Fetch email history
        const emailResponse = await invoiceApi.getInvoiceEmails(currentInvoice.id);
        console.log('[HistoryDialog] Emails received:', emailResponse);
        setEmails(emailResponse.emails || []);
        
        // Fetch invoice history (created/updated timestamps)
        // In a real implementation, you'd make an API call to get this data
        // For now, we'll simulate it with the current invoice data
        
        // Mock timestamps for demo
        const mockCreatedAt = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(); // 7 days ago
        const mockUpdatedAt = new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(); // 2 days ago
        const mockSavedAt = currentInvoice.status !== 'concept' 
          ? new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString() // 5 days ago 
          : undefined;

        setInvoiceHistory({
          created: {
            timestamp: mockCreatedAt,
            formatted: formatRelativeTime(mockCreatedAt, language)
          },
          updated: mockUpdatedAt !== mockCreatedAt ? {
            timestamp: mockUpdatedAt,
            formatted: formatRelativeTime(mockUpdatedAt, language)
          } : undefined,
          saved: mockSavedAt ? {
            timestamp: mockSavedAt,
            formatted: formatRelativeTime(mockSavedAt, language)
          } : undefined
        });
      } catch (err) {
        console.error('Failed to fetch history data:', err);
        setError('Failed to load history. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isOpen, currentInvoice.id, language, currentInvoice.status]);

  const formatDate = (dateString?: string | null, status?: EmailStatus): string => {
    if (!dateString && status === 'planned') {
      return t('common.now');
    }
    if (!dateString) return '';
    
    try {
      return formatRelativeTime(dateString, language);
    } catch (err) {
      console.error('Error formatting date:', err, dateString);
      return t('common.error');
    }
  };

  const generateTimelineItems = (): TimelineItem[] => {
    const items: TimelineItem[] = [];
    
    // Add invoice creation timestamp
    if (invoiceHistory?.created) {
      items.push({
        id: 'created',
        title: t('invoice.history.created'),
        description: t('invoice.history.createdDescription', { invoiceNumber: currentInvoice.invoiceNumber }),
        timestamp: invoiceHistory.created.timestamp,
        formattedTime: invoiceHistory.created.formatted,
        icon: <PlusCircle className="w-4 h-4" />,
        status: 'default'
      });
    }
    
    // Add saved timestamp if available
    if (invoiceHistory?.saved) {
      items.push({
        id: 'saved',
        title: t('invoice.history.saved'),
        description: t('invoice.history.savedDescription'),
        timestamp: invoiceHistory.saved.timestamp,
        formattedTime: invoiceHistory.saved.formatted,
        icon: <Save className="w-4 h-4" />,
        status: 'success'
      });
    }
    
    // Add last updated timestamp if different from created
    if (invoiceHistory?.updated) {
      items.push({
        id: 'updated',
        title: t('invoice.history.updated'),
        description: t('invoice.history.updatedDescription'),
        timestamp: invoiceHistory.updated.timestamp,
        formattedTime: invoiceHistory.updated.formatted,
        icon: <FileEdit className="w-4 h-4" />,
        status: 'default'
      });
    }
    
    // Add payment link events for each paid payment link
    if (currentInvoice.paymentLinks) {
      currentInvoice.paymentLinks.forEach((paymentLink) => {
        if (paymentLink.status === 'paid') {
          const providerName = paymentLink.provider.charAt(0).toUpperCase() + paymentLink.provider.slice(1);
          items.push({
            id: `payment-link-${paymentLink.id}`,
            title: t('invoice.history.paymentReceivedFromProvider', { provider: providerName }),
            description: t('invoice.history.paymentReceivedFromProviderDescription', { provider: providerName }),
            timestamp: new Date(paymentLink.createdAt).toISOString(),
            formattedTime: formatRelativeTime(paymentLink.createdAt, language),
            icon: <CheckCircle className="w-4 h-4" />,
            status: 'success'
          });
        }
      });
    }

    // Add overdue timestamp if invoice is overdue
    if (currentInvoice.paymentStatus === 'overdue' && currentInvoice.dueDate) {
      items.push({
        id: 'overdue',
        title: t('payment.status.overdue'),
        description: t('invoice.history.overdueDescription'),
        timestamp: new Date(currentInvoice.dueDate).toISOString(),
        formattedTime: formatRelativeTime(currentInvoice.dueDate, language),
        icon: <AlertCircle className="w-4 h-4" />,
        status: 'error'
      });
    }

    // Add payment timestamp if invoice is paid, show it as the completion point
    if (currentInvoice.paymentStatus === 'paid' && currentInvoice.paidAt) {
      items.push({
        id: 'paid',
        title: t('payment.status.markedPaid'),
        description: t('invoice.history.markedPaidDescription'),
        timestamp: new Date(currentInvoice.paidAt).toISOString(),
        formattedTime: formatRelativeTime(currentInvoice.paidAt, language),
        icon: <CheckCircle className="w-4 h-4" />,
        status: 'success',
        isEndpoint: true // Mark this as the endpoint of the timeline
      });
    }
    
    // Add email events
    emails.forEach((email) => {
      const isReminder = email.is_reminder === 1;

      if (email.status === 'planned') {
        items.push({
          id: `email-planned-${email.id}`,
          title: isReminder ? t('emailHistory.reminderQueued') : t('emailHistory.queued'),
          description: t('emailHistory.to', { email: email.recipient_email }),
          timestamp: email.scheduled_at,
          formattedTime: formatDate(email.scheduled_at, email.status),
          icon: isReminder ? <Bell className="w-4 h-4" /> : <Calendar className="w-4 h-4" />,
          status: 'pending'
        });
      }

      if (email.status === 'sent') {
        items.push({
          id: `email-sent-${email.id}`,
          title: isReminder ? t('invoice.history.reminderSent') : t('emailHistory.sent'),
          description: isReminder
            ? t('invoice.history.reminderSentDescription', { email: email.recipient_email })
            : t('emailHistory.to', { email: email.recipient_email }),
          timestamp: email.sent_at || email.scheduled_at,
          formattedTime: formatDate(email.sent_at || email.scheduled_at),
          icon: isReminder ? <Bell className="w-4 h-4" /> : <Send className="w-4 h-4" />,
          status: 'success'
        });
      }

      if (email.status === 'failed') {
        items.push({
          id: `email-failed-${email.id}`,
          title: t('emailHistory.failed'),
          description: t('emailHistory.failedDescription', {
            email: email.recipient_email,
            error: email.error || ''
          }),
          timestamp: email.scheduled_at,
          formattedTime: formatDate(email.scheduled_at),
          icon: <AlertCircle className="w-4 h-4" />,
          status: 'error'
        });
      }
    });
    
    // Sort items by timestamp (newest first)
    return items.sort((a, b) => {
      const dateA = new Date(a.timestamp);
      const dateB = new Date(b.timestamp);
      return dateA.getTime() - dateB.getTime();
    });
  };

  return (
    <Dialog isOpen={isOpen} onClose={onClose} size="md" className="max-w-lg">
      <style>{`
        @keyframes spin-slow {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .animate-spin-slow {
          animation: spin-slow 1.5s linear infinite;
        }
      `}</style>
      <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
        <div className="px-6 pt-6 pb-4">
          <h2 className="text-2xl font-semibold text-gray-900">
            {t('email.history')}
          </h2>
          <p className="text-gray-500 mt-1">
            {t('invoice.history.subtitle')}
          </p>
        </div>

        <div className="px-6 pt-5 pb-10 max-h-[500px] overflow-y-auto">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 text-blue-500 animate-spin mb-4" />
              <span className="text-gray-600 font-medium">{t('common.loading')}</span>
            </div>
          ) : error ? (
            <div className="py-12 text-center">
              <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-4" />
              <p className="text-gray-700 font-medium mb-2">{t('invoice.history.loadError')}</p>
              <p className="text-gray-500 mb-4">{t('invoice.history.loadErrorDescription')}</p>
              <motion.button 
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-gray-100 rounded-lg text-gray-700 font-medium hover:bg-gray-200 transition-colors"
              >
                {t('common.refresh')}
              </motion.button>
            </div>
          ) : generateTimelineItems().length === 0 ? (
            <div className="py-12 text-center">
              <div className="w-20 h-20 rounded-full bg-gray-50 flex items-center justify-center mx-auto mb-5 shadow-sm">
                <Clock className="w-10 h-10 text-gray-300" />
              </div>
              <p className="text-gray-800 text-lg font-medium mb-2">{t('invoice.history.empty')}</p>
              <p className="text-gray-500 max-w-sm mx-auto">
                {t('invoice.history.emptyDescription')}
              </p>
            </div>
          ) : (
            <Timeline items={generateTimelineItems()} className="px-2" />
          )}
        </div>
      </div>
    </Dialog>
  );
}; 